// Components/LeftNav.jsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../../assets/image/cms/jyes_logo.svg";
import { useAuth } from "../Admin/Login/AuthContext";

const ACCENT = "#7864F9";

// 전체 메뉴 정의
const ALL_GROUPS = [
  {
    header: "회원 관리",
    items: [
      { key: "users", label: "회원정보", to: "/admin/users" },               // SUPER 전용 라우트 (라우터에서도 보호됨)
      { key: "users-dormant", label: "휴면 회원관리", to: "/admin/sleep/users", disable: true },
      { key: "users-withdrawn", label: "탈퇴 회원관리", to: "/admin/deleted/users" },
    ],
  },
  {
    header: "구독 관리",
    items: [
      { key: "sub-products", label: "결제 내역", to: "/admin/subscription/products", disable: true },
      { key: "sub-payments", label: "환불 내역", to: "/admin/subscription/payments", disable: true },
    ],
  },
  {
    header: "콘텐츠 관리",
    items: [
      { key: "notice", label: "공지사항", to: "/cms/notice" },
      { key: "faq", label: "FAQ", to: "/cms/faq" },
      { key: "inquiry", label: "1:1 문의", to: "/cms/inquiry" },
      { key: "push", label: "푸시 | 팝업 관리", to: "/cms/push/popup", disable: true },
      { key: "events", label: "이벤트 | 프로모션", to: "/cms/events", disable: true },
    ],
  },
  {
    header: "전시 관리",
    items: [
      { key: "push", label: "PUSH 알림 관리", to: "/cms/push", disable: true },
      { key: "pushList", label: "PUSH 발송 내역", to: "/cms/push/list", disable: true },
      { key: "popUp", label: "메인 팝업 관리", to: "/cms/main/popup", disable: true },
      { key: "banner", label: "메인 배너 관리", to: "/cms/banner", disable: true },
    ],
  },
  {
    header: "시스템 관리",
    items: [
      { key: "adminList", label: "관리자계정 | 권한 관리", to: "/admin/list" },
      { key: "policy", label: "회원 정책 관리", to: "/cms/policy", disable: true },
    ],
  },
];

// 권한별로 노출 그룹 필터
function groupsByRole(role) {
  if (role === "SUPER" || role === "MANAGER") return ALL_GROUPS;
  // STAFF → 콘텐츠 관리만
  const content = ALL_GROUPS.find(g => g.header === "콘텐츠 관리");
  return content ? [content] : [];
}

// (1) 가장 구체적인 active 항목 계산
const getActiveTo = (groups, pathname) => {
  const items = groups.flatMap(g => g.items || []);
  // 정확히 일치하거나, 하위 경로(/...)까지 포함해 후보를 모으고
  const candidates = items.filter(it =>
    pathname === it.to || pathname.startsWith(it.to + "/")
  );
  // 가장 긴 경로(가장 구체적)를 선택
  return candidates.sort((a, b) => b.to.length - a.to.length)[0]?.to;
};


const LeftNav = ({
  userName: userNameProp = "홍길동",
  userEmail: userEmailProp = "naver@naver.com",
  onLogout,
  onChangePassword,
  className = "",
}) => {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const role = me?.role || "STAFF";
  const visibleGroups = groupsByRole(role);

  const displayName = me?.username || userNameProp || "관리자";
  const displayEmail = me?.email || userEmailProp || "";

  const activeTo = getActiveTo(visibleGroups, pathname);

  // 기본 로그아웃: 컨텍스트 logout 후 로그인 페이지로
  const defaultLogout = () => {
    try { logout?.(); } catch { }
    navigate("/admin/login", { replace: true });
  };

  const moveConcierge = () => {
    navigate("/cms/concierge")
  }

  const moveConciergeHistory = () => {
    navigate("/concierge/history")
  }

  // 섹션 타이틀용 active/스타일
  const isPathActive = (target) =>
    pathname === target || pathname.startsWith(target + "/");

  const sectionBaseClass =
    "inline-flex items-center h-8 px-3 rounded-lg text-sm font-semibold cursor-pointer transition";

  const sectionClass = (target) =>
    [
      sectionBaseClass,
      isPathActive(target) ? "" : "text-gray-900 hover:bg-gray-50",
    ].join(" ");

  const sectionStyle = (target) =>
    isPathActive(target)
      ? { color: ACCENT, backgroundColor: `${ACCENT}1A` }
      : {};


  // (2) Item: disable이면 div로, active 판단은 activeTo로
  const Item = ({ it }) => {
    const disabled = !!it.disable;
    const active = !disabled && it.to === activeTo;

    if (disabled) {
      return (
        <div
          className={[
            "block rounded-lg px-3 py-2 text-[15px] min-w-0 select-none",
            "text-gray-400 bg-gray-50/50 cursor-not-allowed pointer-events-none",
          ].join(" ")}
          aria-disabled="true"
          tabIndex={-1}
        >
          <span className="truncate">{it.label}</span>
        </div>
      );
    }



    return (
      <Link
        to={it.to}
        className={[
          "block rounded-lg px-3 py-2 text-[15px] transition min-w-0",
          active ? "font-semibold" : "text-gray-800 hover:bg-gray-50",
        ].join(" ")}
        style={active ? { color: ACCENT, backgroundColor: `${ACCENT}1A` } : {}}
      >
        <span className="truncate">{it.label}</span>
      </Link>
    );
  };


  return (
    <aside
      className={[
        "shrink-0 bg-white border-r min-h-screen flex flex-col rounded-tl-lg",
        className,
      ].join(" ")}
    >
      {/* ===== 상단 브랜드 영역 ===== */}
      <div className="px-6 pt-6">
        <div className="pt-6 pb-4">
          <Link to="/" className="block leading-none">
            <span
              className="cms-brand"
              style={{
                fontFamily: '"Sonsie One", cursive',
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: 24,
                lineHeight: "100%",
                letterSpacing: "0px",
                color: "currentColor",
              }}
            >
              Wiz market
            </span>
          </Link>
        </div>

        {/* 인사/아바타 */}
        <div className="mt-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
            <span className="text-sm font-bold text-white bg-gradient-to-tr from-sky-400 to-emerald-400 w-8 h-8 rounded-full inline-flex items-center justify-center">
              W
            </span>
          </div>
          <div className="text-[15px] font-semibold text-gray-900">
            {displayName}님 반갑습니다.
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={onChangePassword}
            className="h-8 px-3 rounded-lg text-sm border bg-gray-50 text-gray-700 hover:bg-gray-100"
          >
            비밀번호 변경
          </button>
          <div className="w-px h-4 bg-gray-200" />
          <button
            onClick={onLogout || defaultLogout}
            className="h-8 px-3 rounded-lg text-sm border bg-gray-50 text-gray-700 hover:bg-gray-100"
          >
            로그아웃
          </button>
        </div>
      </div>

      <div className="mt-5 border-t" />

      {/* 섹션 타이틀 */}

      <div className="px-6 py-5 space-y-2 flex flex-col">
        {/* 로그 통계 */}
        {/* <Link
          to="/cms/statistics"
          className={sectionClass("/cms/statistics")}
          style={sectionStyle("/cms/statistics")}
        >
          로그 통계
        </Link> */}

        {/* 컨시어지 목록 */}
        <div
          className={sectionClass("/cms/concierge")}
          style={sectionStyle("/cms/concierge")}
          onClick={moveConcierge}
        >
          컨시어지 목록
        </div>

        {/* 🔹 컨시어지 히스토리 (비활성 처리) */}
        <div
          className={sectionClass("/concierge/history")}
          style={sectionStyle("/concierge/history")}
          onClick={moveConciergeHistory}
        >
          컨시어지 히스토리
        </div>
        <Link
          to="/concierge/dashboard"
          className={sectionClass("/concierge/dashboard")}
          style={sectionStyle("/concierge/dashboard")}
        >
          컨시어지 현황판
        </Link>
        {(role === "SUPER" || role === "MANAGER") && <Link
          to="/cms/caption-lab"
          className={sectionClass("/cms/caption-lab")}
          style={sectionStyle("/cms/caption-lab")}
        >
          인스타 캡션 테스트머신
        </Link>}
        {(role === "SUPER" || role === "MANAGER") && <Link
          to="/cms/youtube-lab"
          className={sectionClass("/cms/youtube-lab")}
          style={sectionStyle("/cms/youtube-lab")}
        >
          유튜브 자동업로드 테스트머신
        </Link>}
        {(role === "SUPER" || role === "MANAGER") && <Link
          to="/cms/threads-lab"
          className={sectionClass("/cms/threads-lab")}
          style={sectionStyle("/cms/threads-lab")}
        >
          쓰레드 자동업로드 테스트머신
        </Link>}
      </div>



      <div className="border-t" />

      {/* ===== 메뉴 그룹들 ===== */}
      <div className="px-4 pt-3 pb-6 overflow-y-auto">
        {visibleGroups.map((g) => (
          <div key={g.header} className="mt-6 first:mt-3">
            <div className="px-2 text-xs font-semibold text-gray-400 mb-2">
              {g.header}
            </div>
            <nav className="space-y-1">
              {g.items.map((it) => (
                <Item key={it.key} it={it} />
              ))}
            </nav>
          </div>
        ))}
      </div>

      {/* ===== 하단 브랜드/정보 ===== */}
      <div className="mt-auto">
        <div className="px-6">
          <div className="border-t" />
        </div>
        <div className="px-6 py-8">
          <div className="flex items-center gap-3">
            <img src={logo} alt="로고" className="h-10 w-28 rounded" />
          </div>
          <div className="mt-4">
            <div className="font-semibold">Administrator</div>
            <div className="text-sm text-gray-400">{displayEmail}</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default LeftNav;
